import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const ForgotPasswordPage = ({ onNotify }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError('Please enter your email address.');
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send reset link. Please try again.');
      }

      setSubmitted(true);
      onNotify?.('Password reset link sent to your email!', 'success');
    } catch (err) {
      setError(err.message || 'Failed to send reset link. Please try again.');
      onNotify?.(err.message || 'Failed to send reset link. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="auth-page-shell">
        <div className="auth-card">
          <p className="auth-kicker">TK Fashion & Design</p>
          <h2>Check Your Email</h2>
          <p className="auth-subtitle">
            We've sent a password reset link to {email}. Please click the link in your email to reset your password.
          </p>

          <div className="status-banner success" style={{ marginBottom: '20px', textAlign: 'left' }}>
            ✓ Password reset link sent successfully
          </div>

          <p className="mini-note" style={{ marginTop: '20px', marginBottom: '20px' }}>
            The link will expire in 1 hour for security purposes.
          </p>

          <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
            <button
              type="button"
              className="checkout-btn"
              onClick={() => navigate('/login')}
              style={{ marginBottom: '10px' }}
            >
              Back to Login
            </button>
            <button
              type="button"
              className="details-btn"
              onClick={() => navigate('/')}
            >
              Back to Shop
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="auth-page-shell">
      <div className="auth-card">
        <p className="auth-kicker">TK Fashion & Design</p>
        <h2>Forgot Password?</h2>
        <p className="auth-subtitle">
          Enter your email address and we'll send you a link to reset your password.
        </p>

        {error && <div className="status-banner">{error}</div>}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <label>
            Email Address
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              required
              disabled={submitting}
            />
          </label>

          <button type="submit" className="checkout-btn" disabled={submitting}>
            {submitting ? 'Sending Reset Link...' : 'Send Reset Link'}
          </button>
        </form>

        <p className="auth-switch-text">
          Remember your password?{' '}
          <Link to="/login">
            Back to login
          </Link>
        </p>

        <p className="auth-switch-text">
          Don't have an account?{' '}
          <Link to="/register">
            Register here
          </Link>
        </p>
      </div>
    </section>
  );
};

export default ForgotPasswordPage;
